import type {
	ArtistAuthProviderKey,
	ArtistDeploymentRegion,
} from "@/domain/auth/AuthProviderConfiguration";
import { artistSupabase } from "./client";
import { invokeArtistRoute } from "./functions";

export type RegionalAuthProvider = Extract<
	ArtistAuthProviderKey,
	"wechat" | "douyin" | "qq" | "watcha" | "linux-do"
>;

interface RegionalAuthStateResponse {
	readonly schemaVersion: 1;
	readonly authorizationUrl: string;
	readonly state: string;
	readonly expiresAtEpochSeconds: number;
}

interface RegionalAuthSessionResponse {
	readonly schemaVersion: 1;
	readonly userId: string;
	readonly accessToken: string;
	readonly refreshToken: string;
	readonly expiresAtEpochSeconds: number;
}

export function isRegionalAuthProvider(
	provider: ArtistAuthProviderKey,
): provider is RegionalAuthProvider {
	return (
		provider === "wechat" ||
		provider === "douyin" ||
		provider === "qq" ||
		provider === "watcha" ||
		provider === "linux-do"
	);
}

export async function beginRegionalWebSignIn(input: {
	readonly region: ArtistDeploymentRegion;
	readonly provider: RegionalAuthProvider;
	readonly clientRedirectUri: string;
}): Promise<never> {
	if (input.region !== "china") {
		throw new Error("Regional OAuth is available only in the China deployment");
	}
	const state = await invokeArtistRoute<RegionalAuthStateResponse>(
		"/auth-provider-exchange/state",
		{
			body: JSON.stringify({
				schemaVersion: 1,
				provider: input.provider,
				market: "china",
				platform: "web",
				flow: "sign_in",
				clientRedirectUri: input.clientRedirectUri,
			}),
		},
	);
	const authorizationUrl = requireProviderAuthorizationUrl(state.authorizationUrl);
	window.location.assign(authorizationUrl);
	return await new Promise<never>(() => undefined);
}

export function readRegionalAuthCallback():
	| {
			readonly kind: "success";
			readonly provider: RegionalAuthProvider;
			readonly code: string;
			readonly state: string;
	  }
	| { readonly kind: "error"; readonly error: string }
	| null {
	const fragment = new URLSearchParams(window.location.hash.replace(/^#/u, ""));
	const provider = fragment.get("regional_provider") ?? "";
	const code = fragment.get("regional_code") ?? "";
	const state = fragment.get("regional_state") ?? "";
	const error = fragment.get("regional_error") ?? "";
	if (error) return { kind: "error", error };
	if (!provider && !code && !state) return null;
	if (!isRegionalAuthProvider(provider as ArtistAuthProviderKey) || !code || !state) {
		return { kind: "error", error: "invalid_regional_callback" };
	}
	return {
		kind: "success",
		provider: provider as RegionalAuthProvider,
		code,
		state,
	};
}

export async function completeRegionalWebSignIn(input: {
	readonly callback: Extract<
		ReturnType<typeof readRegionalAuthCallback>,
		{ kind: "success" }
	>;
	readonly clientRedirectUri: string;
}): Promise<void> {
	const response = await invokeArtistRoute<RegionalAuthSessionResponse>(
		"/auth-provider-exchange",
		{
			body: JSON.stringify({
				schemaVersion: 1,
				provider: input.callback.provider,
				market: "china",
				platform: "web",
				authCode: input.callback.code,
				state: input.callback.state,
				redirectUri: input.clientRedirectUri,
			}),
		},
	);
	const { error } = await artistSupabase().auth.setSession({
		access_token: response.accessToken,
		refresh_token: response.refreshToken,
	});
	if (error) throw error;
}

export function clearRegionalAuthCallbackFragment(): void {
	const clean = new URL(window.location.href);
	clean.hash = "";
	window.history.replaceState(window.history.state, "", clean);
}

function requireProviderAuthorizationUrl(rawValue: string): string {
	const url = new URL(rawValue);
	if (
		url.protocol !== "https:" ||
		url.username !== "" ||
		url.password !== "" ||
		url.hostname === ""
	) {
		throw new Error("Regional OAuth provider returned an invalid authorization URL");
	}
	return url.toString();
}
